/**
 * Rutas de las landings clásicas (Santa, Reyes, Hada, Ratón Pérez), el hub de
 * guías y las páginas de la app.
 *
 * La primitiva (`getLocalePrefix`, `hrefForSegment`, `PublicLocale`) vive en
 * `lib/locale-prefix.ts` y se re-exporta aquí por compatibilidad; los segmentos
 * de las landings del catálogo genérico, en `lib/generic-landings/paths.ts`.
 */

import {
  getLocalePrefix,
  hrefForSegment,
  type PublicLocale,
} from '@/lib/locale-prefix'
import { GENERIC_LANDING_RESOLVERS } from '@/lib/generic-landings/paths'
// Submódulo de rutas, no el barril: este fichero llega al bundle cliente
// (LanguageSelector) y el barril arrastraría el contenido entero del tracker.
import { GUIDE_LOCALES } from '@/lib/guides/locales'
import { TRACKER_PATH_BY_LOCALE, getSantaTrackerHref } from '@/lib/santa-tracker/paths'
import {
  getFamilyOrigin,
  getHandoverOrigin,
  isCatalogEnabled,
  isFamilyEnabled,
  isHomeFamily,
  isTrackerEnabled,
  type BrandFamily,
} from '@/lib/brands'

export { getLocalePrefix, type PublicLocale }

/** Path segment for tooth fairy landing per locale (no leading slash). */
export const HADA_LANDING_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'hada-de-los-dientes',
  en: 'tooth-fairy',
  pt: 'fada-do-dente',
  it: 'fata-dei-dentini',
  fr: 'fee-des-dents',
  ja: 'ha-no-yousei',
  de: 'zahnfee',
}

/** Path segment for Three Kings landing per locale (no leading slash). */
export const REYES_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'reyes',
  en: 'three-wise-men',
  pt: 'reis',
  it: 're-magi',
  fr: 'rois-mages',
  ja: 'sannin-no-ousama',
  de: 'heilige-drei-koenige',
}

/** Path segment for Ratón Pérez landing per locale (no leading slash). */
export const RATON_PEREZ_LANDING_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'raton-perez',
  en: 'tooth-mouse',
  pt: 'rato-dos-dentes',
  it: 'topolino-dei-dentini',
  fr: 'petite-souris',
  ja: 'ha-no-nezumi',
  de: 'zahnmaus',
}

/** Path segment for the guides hub per locale (no leading slash). */
export const GUIDES_SEGMENT_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'guias',
  en: 'guides',
  pt: 'guias',
  it: 'guide',
  fr: 'guides',
  ja: 'gaido',
  de: 'ratgeber',
}

/**
 * Segmento de la landing «Papá Noel gratis» por locale (sin barra inicial): la
 * página que posiciona las búsquedas de «gratis» (chat, llamada, videollamada,
 * vídeo) y lleva al chat público. Cada idioma tiene su slug, y next-intl
 * (`pathnames`) redirige el slug de otro idioma bajo un prefijo al propio.
 */
export const FREE_SANTA_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'papa-noel-gratis',
  en: 'free-santa-call',
  pt: 'papai-noel-gratis',
  it: 'babbo-natale-gratis',
  fr: 'pere-noel-gratuit',
  de: 'weihnachtsmann-kostenlos',
  ja: 'santa-muryou',
}

/** Segmento del chat bajo su landing: la pantalla de app, que no se indexa. */
export const PUBLIC_SANTA_CHAT_SEGMENT = 'chat'

/** Href de la landing «Papá Noel gratis» en ese locale (es sin prefijo). */
export function getFreeSantaHref(locale: PublicLocale): string {
  return hrefForSegment(FREE_SANTA_PATH_BY_LOCALE, locale)
}

/** Href del chat público (la pantalla de app, tras el consentimiento) en ese locale. */
export function getPublicSantaChatHref(locale: PublicLocale): string {
  return `${getFreeSantaHref(locale)}/${PUBLIC_SANTA_CHAT_SEGMENT}`
}

/** King slugs (same in all locales). */
export const KING_SLUGS = ['melchor', 'gaspar', 'baltasar'] as const
export type KingSlug = (typeof KING_SLUGS)[number]

function isKingSlug(value: string): value is KingSlug {
  return (KING_SLUGS as readonly string[]).includes(value)
}

/**
 * Segmentos de hub de guías que el switcher reconoce. Derivado del registro
 * `GUIDES_SEGMENT_BY_LOCALE`, no reescrito: añadir un idioma solo toca aquel
 * mapa (fue exactamente el drift que dejó a `/it/guide` sin reconocer).
 */
const GUIDES_HUB_SEGMENTS = new Set(Object.values(GUIDES_SEGMENT_BY_LOCALE))

/** Href del hub de guías de un locale (los slugs de guía viven en lib/guides). */
export function getGuidesHubPath(locale: PublicLocale): string {
  const prefix = getLocalePrefix(locale)
  const segment = GUIDES_SEGMENT_BY_LOCALE[locale] ?? GUIDES_SEGMENT_BY_LOCALE.es
  return `${prefix}/${segment}`
}

/** Href for the tooth fairy landing (localized path per locale). */
export function getHadaLandingHref(locale: PublicLocale): string {
  if (isHomeFamily('tooth-fairy')) return getHomeHref(locale)
  return hrefForSegment(HADA_LANDING_PATH_BY_LOCALE, locale)
}

// Los href de cada familia aplican su propio gating de publicación
// (LANDING_LOCALES / TRACKER_LOCALES / GUIDE_LOCALES):
// aquí basta con delegar.
/**
 * Landings con segmento localizado: segmentos conocidos → href por locale,
 * cada una con su gate de marca (ADR-035) y, cuando aplica, un resolutor de
 * sub-segmento propio (Reyes → la página de cada rey, vía `sub`). El switcher
 * (`getPublicPathForLocale`) filtra por `enabled()` en el momento de resolver,
 * no al construir la tabla: así lee la marca activa en cada llamada, igual que
 * el resto de `lib/brands`.
 */
const LANDING_RESOLVERS: Array<{
  segments: string[]
  href: (locale: PublicLocale) => string
  enabled: () => boolean
  sub?: (locale: PublicLocale, rest: string) => string | undefined
}> = [
  {
    segments: Object.values(HADA_LANDING_PATH_BY_LOCALE),
    href: getHadaLandingHref,
    enabled: () => isFamilyEnabled('tooth-fairy'),
  },
  {
    segments: Object.values(REYES_PATH_BY_LOCALE),
    href: getReyesHref,
    enabled: () => isFamilyEnabled('reyes'),
    sub: (locale, rest) => (isKingSlug(rest) ? getReyHref(locale, rest) : undefined),
  },
  {
    segments: Object.values(RATON_PEREZ_LANDING_PATH_BY_LOCALE),
    href: getRatonPerezLandingPath,
    enabled: () => isFamilyEnabled('raton-perez'),
  },
  {
    segments: Object.values(TRACKER_PATH_BY_LOCALE),
    href: getSantaTrackerHref,
    enabled: isTrackerEnabled,
  },
  ...GENERIC_LANDING_RESOLVERS.map((resolver) => ({ ...resolver, enabled: isCatalogEnabled })),
]

function guidesHubHrefForLocale(newLocale: PublicLocale): string {
  // Fuente única de publicación: GUIDE_LOCALES (lib/guides/locales). Sin guías
  // publicadas en el destino, el switcher lleva a su home, no a un hub 404.
  if (GUIDE_LOCALES.some((locale) => locale === newLocale)) {
    return getGuidesHubPath(newLocale)
  }
  return getHomeHref(newLocale)
}

/**
 * For locale switcher: given a path without locale (e.g. /tooth-fairy), returns the path
 * to use for the new locale. Use when the path is a localized pathname (e.g. hada, reyes).
 *
 * Para partir de un pathname que TODAVÍA lleva prefijo de locale (lo habitual en
 * un componente, vía `usePathname`) usa `getEquivalentPath` en
 * `lib/equivalent-path.ts`, que quita el prefijo antes de llamar aquí.
 */
export function getPublicPathForLocale(pathWithoutLocale: string, newLocale: PublicLocale): string {
  const normalized = pathWithoutLocale.startsWith('/') ? pathWithoutLocale : `/${pathWithoutLocale}`
  const segment = normalized === '/' ? '' : normalized.slice(1)
  // El home entra en la misma cadena de resolución por familia de ruta que el
  // resto. Sin este caso el return final concatenaba prefijo + "/" y daba
  // "/en/", un 308 a "/en" con `trailingSlash: false` (el default) en el cambio
  // de idioma más frecuente que hay.
  if (segment === '') return getHomeHref(newLocale)

  const landing = resolveLandingHref(segment, newLocale)
  if (landing) return landing

  const familyHref = resolveLandingFamily(segment, newLocale)
  if (familyHref) return familyHref
  const prefix = getLocalePrefix(newLocale)
  return `${prefix}${normalized}`
}

/**
 * Href de una landing del registro (o de su sub-segmento, p. ej.
 * /reyes/melchor) en el locale destino; undefined si el segmento no es de
 * ninguna landing habilitada.
 */
function resolveLandingHref(segment: string, newLocale: PublicLocale): string | undefined {
  const [first, ...rest] = segment.split('/')
  const resolver = LANDING_RESOLVERS.find((r) => r.enabled() && r.segments.includes(first))
  if (!resolver) return undefined
  if (rest.length === 0) return resolver.href(newLocale)
  return resolver.sub?.(newLocale, rest.join('/'))
}

/**
 * Hub de guías editoriales (ver lib/guides), cuyo slug es propio de cada
 * idioma así que el switcher lleva al hub del locale destino. Las familias con
 * lógica de sub-segmento propia (Reyes) resuelven en `LANDING_RESOLVERS` vía
 * `resolveLandingHref`. No se importa el registro de guías aquí a propósito:
 * este helper llega al bundle cliente (LanguageSelector) y arrastraría todo el
 * contenido editorial.
 */
function resolveLandingFamily(segment: string, newLocale: PublicLocale): string | undefined {
  const firstSegment = segment.split('/')[0]
  if (GUIDES_HUB_SEGMENTS.has(firstSegment)) {
    return guidesHubHrefForLocale(newLocale)
  }
  return undefined
}

/** Href for the Three Kings landing (localized path per locale). */
export function getReyesHref(locale: PublicLocale): string {
  if (isHomeFamily('reyes')) return getHomeHref(locale)
  return hrefForSegment(REYES_PATH_BY_LOCALE, locale)
}

/** Href for a single king page (localized path per locale; king slug unchanged). */
export function getReyHref(locale: PublicLocale, king: KingSlug): string {
  const prefix = getLocalePrefix(locale)
  const path = REYES_PATH_BY_LOCALE[locale] ?? REYES_PATH_BY_LOCALE.es
  return prefix ? `${prefix}/${path}/${king}` : `/${path}/${king}`
}

/**
 * Path for the Ratón Pérez landing. Cuando la familia es la home de la marca
 * (ratonperez.app) la landing vive en `/`: enlazar a `/raton-perez` sería
 * apuntar a una redirección 308.
 */
export function getRatonPerezLandingPath(locale: PublicLocale): string {
  if (isHomeFamily('raton-perez')) return getHomeHref(locale)
  return hrefForSegment(RATON_PEREZ_LANDING_PATH_BY_LOCALE, locale)
}

/** Href for the home/ Santa landing. */
export function getHomeHref(locale: PublicLocale): string {
  const prefix = getLocalePrefix(locale)
  return prefix || '/'
}

/**
 * Ruta local de una familia clásica. El `switch` es deliberado: `BrandFamily`
 * comparte exactamente las cuatro claves del dominio y el compilador obliga a
 * revisar este único resolutor cuando se añade una familia.
 */
export function getFamilyLandingPath(family: BrandFamily, locale: PublicLocale): string {
  switch (family) {
    case 'santa':
      return getHomeHref(locale)
    case 'reyes':
      return getReyesHref(locale)
    case 'tooth-fairy':
      return getHadaLandingHref(locale)
    case 'raton-perez':
      return getRatonPerezLandingPath(locale)
  }
}

/** URL absoluta de una familia en el dominio que la sirve de forma canónica. */
export function getCanonicalFamilyLandingHref(family: BrandFamily, locale: PublicLocale): string {
  const origin = getFamilyOrigin(family)
  const path = getHandoverOrigin(family) ? getHomeHref(locale) : getFamilyLandingPath(family, locale)
  return `${origin}${path}`
}

/**
 * Href para ENLAZAR a la landing de una familia: su ruta local si esta marca la
 * sirve, y la URL absoluta del dominio destino si está traspasada (ADR-035 fase
 * 8). La dueña de una familia traspasada la sirve en `/` —invariante que
 * comprueba `validateHandover`—, así que el destino es su prefijo de locale.
 *
 * Es la contrapartida en enlaces de `getFamilyOrigin`, que hace lo mismo con el
 * canonical: sin enlaces salientes el dominio destino solo se descubre por el
 * sitemap, y Google lo deja en «Descubierta: actualmente sin indexar».
 *
 * Precondición: la marca activa sirve la familia o la traspasa. Fuera de ahí no
 * hay enlace que devolver y la función lo dice en voz alta (ver el cuerpo).
 */
export function getFamilyLandingHref(family: BrandFamily, locale: PublicLocale): string {
  const origin = getHandoverOrigin(family)
  if (origin) return `${origin}${getHomeHref(locale)}`
  // Precondición, no defensa: sin traspaso la ruta local solo existe si esta marca
  // sirve la familia. Para una que ni sirve ni traspasa (`reyes` en ratonperez.app)
  // devolver la ruta local sería enlazar a un `notFound()`, y en silencio. El origen
  // del dueño tampoco sirve aquí: la ruta en su dominio depende de si esa familia es
  // su `home`, que es otra pregunta (ver el hilo de la review de #463).
  if (!isFamilyEnabled(family)) {
    throw new Error(`getFamilyLandingHref: la marca activa ni sirve ni traspasa la familia ${family}`)
  }
  return getFamilyLandingPath(family, locale)
}


/**
 * Índice del sitio para agentes en Markdown (convención llms.txt). Vive aquí,
 * con el resto de rutas públicas, para que el layout pueda anunciarlo sin
 * arrastrar el renderizador ni los catálogos de copy (ver lib/agent-index).
 */
export function getLlmsTxtPath(locale: PublicLocale): string {
  return `${getLocalePrefix(locale)}/llms.txt`
}

/** Href for the mobile native app landing (Capacitor). */
export function getMobileHref(locale: PublicLocale): string {
  const prefix = getLocalePrefix(locale)
  return prefix ? `${prefix}/mobile` : '/mobile'
}
